import { Autocomplete, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { netsuiteSyncWizardSelectors } from '@vy/app-selectors';
import type { NetSuiteCustomer } from '@vy/types';
import { INTEGRATION_PROVIDERS, type ErpIntermediateMetadata } from '@vy/types';
import { isEmptyArray, isEmptyString, isNotEmptyArray, isNotEmptyString, uniqueExtract } from '@vy/utils';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { integrationAutocompleteSx } from '../../../../../../../components/integrations/integration-field-styles';
import { useNetSuiteCustomers } from '../../../../../../../hooks/requests/integrations';
import { useDebounce } from '../../../../../../../hooks/use-debounce';
import { STEP_TITLE_SX } from '../constants/net-suite-sync-wizard-styles';

const DEBOUNCE_DELAY = 800;

type CustomerConnectionStepProps = {
  selectedCustomer: ErpIntermediateMetadata | null;
  onCustomerSelect: (customer: ErpIntermediateMetadata | null) => void;
};

export const CustomerConnectionStep: FC<CustomerConnectionStepProps> = ({
  selectedCustomer,
  onCustomerSelect,
}) => {
  const [searchInput, setSearchValue] = useState<string | undefined>(undefined);
  const [allCustomers, setAllCustomers] = useState<NetSuiteCustomer[]>([]);
  const debouncedSearchValue = useDebounce(searchInput, DEBOUNCE_DELAY);

  const { data: { customers } = {}, isFetching } = useNetSuiteCustomers(debouncedSearchValue, {
    keepPreviousData: true,
  });

  useEffect(() => {
    if (isNotEmptyArray(customers)) {
      setAllCustomers((prevCustomers) => mergeUniqueCustomers(prevCustomers, customers));
    }
  }, [customers]);

  const customersErpData = useMemo(() => netSuiteCustomersToErpData(allCustomers), [allCustomers]);

  const handleCustomerChange = (_: React.SyntheticEvent, value: ErpIntermediateMetadata | null) => {
    onCustomerSelect(value);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isEmptyString(e.target.value)) {
      onCustomerSelect(null);
    }

    setSearchValue(e.target.value);
  };

  return (
    <Stack data-vy={netsuiteSyncWizardSelectors.customerStep} sx={{
      gap: 2
    }}>

      <Typography sx={STEP_TITLE_SX}>Please select the NS Customer ID to connect</Typography>
      <Autocomplete
        data-vy={netsuiteSyncWizardSelectors.customerInput}
        options={customersErpData}
        getOptionLabel={(option) => option?.erpName ?? ''}
        getOptionDisabled={(option) => option.erpId === selectedCustomer?.erpId}
        onChange={handleCustomerChange}
        value={selectedCustomer}
        sx={integrationAutocompleteSx}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select NS Customer ID"
            onChange={handleSearchChange}
            variant="outlined"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {isFetching && <CircularProgress color="inherit" size={20} />}
                    {params.slotProps.input.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Stack>
  );
};

function mergeUniqueCustomers(existingCustomers: NetSuiteCustomer[], newCustomers: NetSuiteCustomer[]) {
  const existingIds = uniqueExtract(existingCustomers, 'id');

  return [...existingCustomers, ...newCustomers.filter((customer) => !existingIds.includes(customer.id))];
}

function netSuiteCustomersToErpData(customers: NetSuiteCustomer[] | undefined) {
  if (isEmptyArray(customers)) {
    return [];
  }

  return customers.map<ErpIntermediateMetadata>(({ id, entityId, companyName, aliasName }) => ({
    erpId: id,
    erpName: formatErpName(entityId, companyName, aliasName),
    displayId: entityId,
    integrationSource: INTEGRATION_PROVIDERS.netSuite,
  }));
}

function formatErpName(entityId: string, companyName: string, aliasName: string) {
  const formattedName = isNotEmptyString(companyName) ? companyName : aliasName;

  return isEmptyString(formattedName) ? entityId : `${entityId} ${formattedName}`;
}
